/**
 * Composant de groupe de boutons radio pour les formulaires React Hook Form.
 * Il affiche les options les unes sous les autres, gère l'état d'erreur
 * et permet d'exécuter des callbacks personnalisés sur le changement et le blur.
 */

import { FieldError, FieldValues, Path, UseFormRegister } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type RadioOption = {
  value: string
  label: string
  disabled?: boolean
}

type FormRadioGroupProps<TFormValues extends FieldValues> = {
  fieldName: Path<TFormValues>
  register: UseFormRegister<TFormValues>
  options: RadioOption[]
  wrapperClassName?: string
  radioWrapperClassName?: string
  radioClassName?: string
  labelClassName?: string
  wrapperErrorClassName?: string
  error?: FieldError
  readonly?: boolean
  onChange?: (value: string) => void
  onBlur?: () => void
  required?: boolean
}

export const FormRadioGroup = <TFormValues extends FieldValues>({
  fieldName,
  register,
  options,
  wrapperClassName,
  radioWrapperClassName,
  radioClassName,
  labelClassName,
  wrapperErrorClassName,
  error,
  readonly = false,
  onChange,
  onBlur,
  required = true,
}: FormRadioGroupProps<TFormValues>) => {
  const { t } = useTranslation()

  const registration = register(fieldName, { required })

  return (
    <div className={wrapperClassName ?? ''}>
      <div className="flex flex-col gap-2">
        {options.map((option) => (
          <label
            key={option.value}
            className={`${radioWrapperClassName ?? ''} flex items-center gap-2 ${
              readonly || option.disabled
                ? 'opacity-60 cursor-not-allowed'
                : 'cursor-pointer'
            }`}
          >
            <input
              type="radio"
              value={option.value}
              disabled={readonly || option.disabled}
              className={`${radioClassName ?? ''} ${error ? 'error' : ''}`}
              {...registration}
              onChange={(e) => {
                registration.onChange(e)
                onChange?.(e.target.value)
              }}
              onBlur={(e) => {
                registration.onBlur(e)
                onBlur?.()
              }}
            />
            <span className={labelClassName ?? ''}>{option.label}</span>
          </label>
        ))}
      </div>

      <div
        className={`${wrapperErrorClassName ?? ''} ${error ? 'block' : 'hidden'}`}
      >
        {error?.type === 'required' && (
          <p className="form-error">{t('global.form.error.mandatory')}</p>
        )}
        {error?.type === 'custom' && (
          <p className="form-error">{error.message}</p>
        )}
      </div>
    </div>
  )
}
